import React, { FC, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';

import { IJobDTO, IJobValueDTO } from 'src/api/dtos';
import { Autocomplete, ChevronDownIcon, CloseFilledIcon, Modal, SearchIcon, SuitcaseIcon } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';

interface Props {
  jobs: IJobDTO[];
  loading: boolean;
  open: boolean;
  onClose: () => void;
  onSubmitPunch: (values: string[]) => void;
}

export const JobPunchModal: FC<Props> = ({ loading, open, onClose, jobs, onSubmitPunch }) => {
  const { t } = useTranslate();
  const [jobSelections, setJobSelections] = useState<Record<string, string>>({});
  const selectionList = Object.values(jobSelections);
  const disabled = selectionList.length !== jobs.length || loading;

  useEffect(() => {
    if (open) {
      return;
    }

    setJobSelections({});
  }, [open]);

  const handleSelection = (value: string | undefined, index: number) => {
    const existingSelection = jobSelections[index];

    if (existingSelection && !value?.length) {
      const clone = cloneDeep(jobSelections);
      delete clone[index];
      setJobSelections(clone);
      return;
    }

    setJobSelections({ ...jobSelections, [index]: value });
  };

  const handleSubmit = () => {
    onSubmitPunch(selectionList);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      modalHeaderProps={{
        title: t('punches.jobPunch'),
        message: t('punches.toSubmitAJobPunchMakeSureToSelectAValueForEachOfTheOptions'),
        onClose,
      }}
      modalFooterProps={{
        closeText: t('global.close'),
        submitText: t('punches.submitJobPunch'),
        closeIcon: <CloseFilledIcon />,
        submitIcon: <SuitcaseIcon />,
        loading,
        disabled,
        onClose,
        onSubmit: handleSubmit,
      }}
    >
      {!jobs.length ? (
        <div className="my-5 d-flex align-items-center justify-content-center">
          <CircularProgress />
        </div>
      ) : (
        jobs.map((job, index) => {
          return (
            <SelectRow
              key={index}
              name={job.name ?? ''}
              values={job.values}
              onSelection={(value) => handleSelection(value, index)}
            />
          );
        })
      )}
    </Modal>
  );
};

interface JobLevelProps {
  name: string;
  values: IJobValueDTO[];
  onSelection: (value: string | undefined) => void;
}

const SelectRow: FC<JobLevelProps> = ({ values, name, onSelection }) => {
  const { t } = useTranslate();

  return (
    <div className="mb-3 w-75">
      <p className="mb-2 fw-medium required">{name}</p>
      <Autocomplete
        size="small"
        className="w-100"
        placeholder={t('global.selectValueOrSearchValue')}
        popupIcon={<ChevronDownIcon color="#C2C9D1" />}
        popupStartIcon={<SearchIcon />}
        onChange={(_, data) => onSelection(data?.value)}
        options={values}
        getOptionLabel={({ description, value }) => `${value}: ${description}`}
      />
    </div>
  );
};
